import { Box, CssBaseline } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

// My components
import NotSeenAchievementsSnack from '../hall/components/NotSeenAchievementsSnack';
import NotSeenAchievementsModal from '../hall/modals/NotSeenAchievementsModal';
import Navbar from './Navbar';
import SideMenu from './SideMenu';

// Custom hook for achievement colors
import { useAchievementColors } from '../hall/hooks/useAchievementColors';

// Styled components
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

function Layout(props) {
  const {
    userRole,
    children,
    getUserRoles,
    isLoadingRole,
    isLoadingProjects,
    isLoadingUsers,
    isLoadingScopes,
    isLoadingTeams,
    isLoadingReleases,
    isLoadingSlackChannels,
    isLoadingCoverageList,
    getTeams,
    getSlackChannels,
    isLoadingGAP,
    isLoadingFindFiles,
    // Achievement props
    notSeenAchievements,
    achievementsTypes,
    getNotSeenAchievements,
    patchNotSeenAchievements,
    getAchievementsTypes,
  } = props;

  const [open, setOpen] = useState(false);
  const [showSnack, setShowSnack] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const hasShownSnackRef = useRef(false);

  // Use dynamic achievement colors
  const getAchievementColors = useAchievementColors(achievementsTypes);

  const handleDrawer = () => {
    setOpen(!open);
  };

  const handleShowModal = () => {
    setShowModal(true);
    // Mark achievements as seen when modal opens
    if (notSeenAchievements && notSeenAchievements.length > 0) {
      const achievementProgressIds = notSeenAchievements.map(achievement => achievement.achievement_progress_id);
      patchNotSeenAchievements({ achievement_progress_ids: achievementProgressIds });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCloseSnack = () => {
    setShowSnack(false);
    hasShownSnackRef.current = true;
  };

  useEffect(() => {
    getUserRoles();
    getTeams();
    getSlackChannels();
    getAchievementsTypes();
  }, [getUserRoles, getTeams, getSlackChannels, getAchievementsTypes]);

  // Check for not seen achievements and show snack (only once)
  useEffect(() => {
    if (
      notSeenAchievements &&
      notSeenAchievements.length > 0 &&
      !showSnack &&
      !showModal &&
      !hasShownSnackRef.current
    ) {
      setShowSnack(true);
    }
  }, [notSeenAchievements, showSnack, showModal, userRole]);

  // Load not seen achievements on mount
  useEffect(() => {
    getNotSeenAchievements();
  }, [getNotSeenAchievements]);

  // Reset snack state when achievements are cleared
  useEffect(() => {
    if (!notSeenAchievements || notSeenAchievements.length === 0) {
      hasShownSnackRef.current = false;
    }
  }, [notSeenAchievements]);

  return (
    <Box style={{ display: 'flex' }}>
      <CssBaseline />
      {/* Top navigation bar */}
      <Navbar
        open={open}
        handleDrawer={handleDrawer}
        isLoading={
          isLoadingRole ||
          isLoadingProjects ||
          isLoadingUsers ||
          isLoadingScopes ||
          isLoadingTeams ||
          isLoadingSlackChannels ||
          isLoadingReleases ||
          isLoadingCoverageList ||
          isLoadingGAP ||
          isLoadingFindFiles
        }
      />

      {/* Left navigation bar */}
      <SideMenu
        open={open}
        handleDrawer={handleDrawer}
        DrawerHeader={DrawerHeader}
        isAdmin={userRole?.admin}
      />

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />

        {children}
      </Box>

      {/* Not Seen Achievements Snack */}
      <NotSeenAchievementsSnack
        open={showSnack}
        onClose={handleCloseSnack}
        onViewAchievements={handleShowModal}
        achievementsCount={notSeenAchievements?.length || 0}
      />

      {/* Not Seen Achievements Modal */}
      <NotSeenAchievementsModal
        open={showModal}
        onClose={handleCloseModal}
        achievements={notSeenAchievements || []}
        getAchievementColors={getAchievementColors}
      />
    </Box>
  );
}

export default Layout;
